import type { IAdminUserRepository } from "@/application/ports/repositories";
import type { AdminUser, AdminUserDetails, UserFilters, UserListResponse } from "@/domain/admin";
import { apiClient } from "../client";

// Interface for raw user data coming from the backend API
interface BackendUserResponse {
  uuid?: string;
  id?: string;
  name?: string;
  full_name?: string;
  username?: string;
  email: string;
  avatar?: string;
  profile_picture?: string;
  avatar_url?: string;
  profile_picture_signed_url?: string;
  status?: string;
  is_active?: boolean;
  is_admin?: boolean;
  is_platform_admin?: boolean;
  created_at?: string;
  updated_at?: string;
  last_login?: string;
  last_login_at?: string;
  organizations_count?: number;
  organization_count?: number;
}

export class AdminUserRepository implements IAdminUserRepository {
  private basePath = "/admin/users";

  async getUsers(filters?: UserFilters): Promise<UserListResponse> {
    const params = new URLSearchParams();

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          params.append(key, value.toString());
        }
      });
    }

    const url = params.toString() ? `${this.basePath}?${params.toString()}` : this.basePath;
    const response = await apiClient.get<UserListResponse | BackendUserResponse[]>(url);

    // If response.data is an array, wrap it in the expected format
    if (Array.isArray(response.data)) {
      const mappedUsers: AdminUser[] = response.data.map((user: BackendUserResponse) => ({
        uuid: user.uuid || user.id || "",
        name: user.name || user.full_name || user.username || "",
        email: user.email || "",
        avatar: user.avatar || user.profile_picture || "",
        profile_picture_signed_url: user.profile_picture_signed_url || user.avatar_url || "",
        status: (user.status === "active" || user.status === "inactive" ? user.status : "active") as
          | "active"
          | "inactive",
        is_platform_admin: user.is_platform_admin || user.is_admin || false,
        created_at: user.created_at || new Date().toISOString(),
        updated_at: user.updated_at || new Date().toISOString(),
        last_login_at: user.last_login_at || user.last_login || undefined,
        organization_count: user.organization_count || user.organizations_count || 0,
      }));

      return {
        users: mappedUsers,
        total: mappedUsers.length,
        page: 1,
        limit: 20,
        total_pages: 1,
      };
    }

    // If response.data has the expected structure
    if (response.data && typeof response.data === "object" && "users" in response.data) {
      return response.data as UserListResponse;
    }

    // Fallback: return empty response
    return {
      users: [],
      total: 0,
      page: 1,
      limit: 20,
      total_pages: 1,
    };
  }

  async getUser(uuid: string): Promise<AdminUserDetails> {
    const response = await apiClient.get<AdminUserDetails>(`${this.basePath}/${uuid}`);
    return response.data;
  }
  async toggleUserStatus(uuid: string): Promise<AdminUser> {
    const response = await apiClient.patch<AdminUser>(`${this.basePath}/${uuid}/status`);
    return response.data;
  }
  async sendResetPasswordEmail(uuid: string): Promise<{ message: string }> {
    const response = await apiClient.post<{ message: string }>(
      `${this.basePath}/${uuid}/send-reset-password-email`,
    );
    return response.data;
  }
}
